import React, { useCallback, useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { NO_KEY, YES_KEY } from '@/common/constants';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';
import { useStore } from '@/modules/employer/store/useSetupEmployer.store';

import { CardSetupPayload } from '../../../SetupEmployer.types';
import FieldLabelWithDescription from '../components/FieldLabelWithDescription';

export const usePendingDocsEscalationFields = () => {
  const cardsSetup = useStore((state) => state.cardsSetup);
  const setCardSetup = useStore((state) => state.setCardSetup);
  const { formattedData } = useGetEmployer();

  const onChangeValues = useCallback((val: CardSetupPayload) => setCardSetup(val), [setCardSetup]);

  const fields = useMemo<Field[]>(
    () => [
    {
      name: 'documentsNeededReminder',
      type: FieldTypes.Radio,
      expandItems: true,
      label: (
        <FieldLabelWithDescription
          title="Documents needed reminder"
          desc="Send reminder emails based on the configured frequency until the required action is completed (every X days)"
        />
      ),
      defaultValue: formattedData?.overridableProperties?.documentsNeededReminder,
      value: cardsSetup?.documentsNeededReminder,
      onChangeValues,
      options: [
        { label: 'Yes', value: YES_KEY },
        { label: 'No', value: NO_KEY },
      ],
      subFields: [
        {
          name: 'documentsNeededReminderDays',
          type: FieldTypes.Number,
          label: ' ',
          placeholder: 'Enter # of days',
          parentValue: YES_KEY,
          validator: VALIDATORS.REQUIRED_POSITIVE_NUMBER_ONLY,
          defaultValue: formattedData?.overridableProperties?.documentsNeededReminderDays,
          value: cardsSetup?.documentsNeededReminderDays,
          onChangeValues,
        },
      ],
    },
    {
      name: 'suspendCard',
      type: FieldTypes.Radio,
      expandItems: true,
      label: (
        <FieldLabelWithDescription
          title="Suspend card"
          desc="Suspend the card purse if documentation is not provided for a card claim within the configured number of days"
        />
      ),
      defaultValue: formattedData?.overridableProperties?.suspendCard,
      value: cardsSetup?.suspendCard,
      onChangeValues,
      options: [
        { label: 'Yes', value: YES_KEY },
        { label: 'No', value: NO_KEY },
      ],
      subFields: [
        {
          name: 'suspendCardDays',
          type: FieldTypes.Number,
          label: ' ',
          placeholder: 'Enter # of days',
          parentValue: YES_KEY,
          validator: VALIDATORS.REQUIRED_POSITIVE_NUMBER_ONLY,
          defaultValue: formattedData?.overridableProperties?.suspendCardDays,
          value: cardsSetup?.suspendCardDays,
          onChangeValues,
        },
      ],
    },
    {
      name: 'suspensionThreshold',
      type: FieldTypes.Radio,
      expandItems: true,
      label: (
        <FieldLabelWithDescription
          title="Suspension threshold"
          desc="Suspend the card purse when the total outstanding repayment amount across all claims exceeds the configured suspension threshold"
        />
      ),
      defaultValue: formattedData?.overridableProperties?.suspensionThreshold,
      value: cardsSetup?.suspensionThreshold,
      onChangeValues,
      options: [
        { label: 'Yes', value: YES_KEY },
        { label: 'No', value: NO_KEY },
      ],
      subFields: [
        {
          name: 'suspensionThresholdAmount',
          type: FieldTypes.Number,
          label: ' ',
          placeholder: 'Enter amount',
          parentValue: YES_KEY,
          validator: VALIDATORS.REQUIRED_POSITIVE_NUMBER_ONLY,
          defaultValue: formattedData?.overridableProperties?.suspensionThresholdAmount,
          value: cardsSetup?.suspensionThresholdAmount,
          onChangeValues,
        },
      ],
    },
    ],
    [onChangeValues, cardsSetup, formattedData]
  );

  return fields;
};

export default usePendingDocsEscalationFields;
